import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
} from '@mui/material';
import { AttachMoney, Receipt, Warning } from '@mui/icons-material';
import { BRAND } from '../../config/brand';
import { useGetInvoicesQuery, useGetRevenueReportQuery } from '../../api/financialApi';
import EntityDataTable, { EntityColumn } from '../common/EntityDataTable';

export const FinancialDashboard: React.FC = () => {
  const { data: invoices = [], isLoading } = useGetInvoicesQuery();
  const { data: revenue, isLoading: revLoading } = useGetRevenueReportQuery();

  const columns: EntityColumn<Record<string, any>>[] = [
    { id: 'invoiceNumber', label: 'Invoice #' },
    {
      id: 'customer',
      label: 'Customer',
      render: (row) => row.customer?.name || '—',
    },
    {
      id: 'total',
      label: 'Total',
      render: (row) => `$${Number(row.total).toLocaleString()}`,
    },
    {
      id: 'dueDate',
      label: 'Due Date',
      render: (row) => row.dueDate ? new Date(row.dueDate).toLocaleDateString() : '—',
    },
  ];

  if (revLoading) return <CircularProgress sx={{ color: BRAND.colors.navy }} />;

  return (
    <Box>
      <Typography variant="h4" sx={{ color: BRAND.colors.navy, fontWeight: 700, mb: 3 }}>
        Financial Dashboard
      </Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ borderTop: `4px solid ${BRAND.colors.navy}` }}>
            <CardContent sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <AttachMoney sx={{ fontSize: 40, color: BRAND.colors.navy }} />
              <Box>
                <Typography variant="caption">Total Revenue</Typography>
                <Typography variant="h5" fontWeight={700}>
                  ${Number(revenue?.totalRevenue || 0).toLocaleString()}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ borderTop: '4px solid #ed6c02' }}>
            <CardContent sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Receipt sx={{ fontSize: 40, color: '#ed6c02' }} />
              <Box>
                <Typography variant="caption">Accounts Receivable</Typography>
                <Typography variant="h5" fontWeight={700}>
                  ${Number(revenue?.accountsReceivable || 0).toLocaleString()}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ borderTop: '4px solid #d32f2f' }}>
            <CardContent sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Warning sx={{ fontSize: 40, color: '#d32f2f' }} />
              <Box>
                <Typography variant="caption">Overdue Invoices</Typography>
                <Typography variant="h5" fontWeight={700}>{Number(revenue?.overdueCount ?? 0)}</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <EntityDataTable
        title="Invoices"
        data={invoices as any[]}
        columns={columns}
        statusField="status"
        statusColors={{
          draft: 'default',
          sent: 'info',
          paid: 'success',
          overdue: 'error',
          cancelled: 'warning',
        }}
        searchFields={['invoiceNumber']}
        loading={isLoading}
      />
    </Box>
  );
};

export default FinancialDashboard;
